import { DefineComponent } from 'vue';
import { VueConstructor } from 'vue-class-component';
import { RouteLocationRaw } from 'vue-router';

import { StandardOption } from './Component';

export type FilterChosen$ThesaurusDescOption<A extends string = string> = Omit<
  StandardOption<A>,
  'label'
> & {
  tkey: string;
};

export type FieldDescription$Thesaurus<E extends string = string> = Array<StandardOption<E>>;
export type FieldDescription$ThesaurusDescription<A extends string = string> =
  FilterChosen$ThesaurusDescOption<A>[];

type FieldDescription$Common<E, K = keyof E> = {
  label: string;
  field: K | [K, string];
};

export enum FieldDescription$Type {
  STRING = 'string',
  IMAGE = 'image',
  DATE = 'date',
  THUMBNAIL = 'thumbnail',
  URL = 'url',
  THESAURUS = 'thesaurus',
  ROUTE = 'route',
  CUSTOM = 'custom',
}

export enum FieldDescription$Filter {
  STRING = 'string',
  PERIOD = 'date-period',
  LOOKUP = 'lookup',
  THESAURUS = 'thesaurus',
}

const SIMPLE: FieldDescription$Type[] = [
  FieldDescription$Type.STRING,
  FieldDescription$Type.IMAGE,
  FieldDescription$Type.DATE,
  FieldDescription$Type.THUMBNAIL,
];

type FieldDescription$SimpleField<E> = {
  type: FieldDescription$Type.STRING | FieldDescription$Type.IMAGE | FieldDescription$Type.DATE;
  filter?: FieldDescription$Filter;
  sortable?: boolean;
  modifier?: (value: any) => string;
} & FieldDescription$Common<E>;

type FieldDescription$URLField<E> = {
  type: FieldDescription$Type.URL;
  host?: string;
} & FieldDescription$Common<E>;

type FieldDescription$ThumbnailField<E> = {
  type: FieldDescription$Type.THUMBNAIL;
} & FieldDescription$Common<E>;

type FieldDescription$EnumerableField<E, O extends string = string> = {
  type: FieldDescription$Type.THESAURUS;
  thesaurus: FieldDescription$Thesaurus<O>;
} & FieldDescription$Common<E>;

type FieldDescription$RouteField<E> = {
  type: FieldDescription$Type.ROUTE;
  route: RouteLocationRaw;
} & FieldDescription$Common<E>;

type FieldDescription$Custom<E> = {
  type: FieldDescription$Type.CUSTOM;
  component: VueConstructor | DefineComponent<unknown, unknown, unknown>;
  props?: Dictionary<unknown>;
} & FieldDescription$Common<E>;

type BaseFieldDescription<O extends string = string> =
  | { type: FieldDescription$Type }
  | {
      type: FieldDescription$Type;
      filter: FieldDescription$Filter;
      thesaurus: FieldDescription$Thesaurus<O>;
    };

export type FieldDescription<E = Entity, O extends string = string> = BaseFieldDescription<O> &
  (
    | FieldDescription$SimpleField<E>
    | FieldDescription$EnumerableField<E>
    | FieldDescription$URLField<E>
    | FieldDescription$Custom<E>
    | FieldDescription$ThumbnailField<E>
    | FieldDescription$RouteField<E>
  );

export function isSimpleField<E>(
  desc: FieldDescription<E>
): desc is FieldDescription$SimpleField<E> {
  return SIMPLE.includes(desc.type);
}

export function isEnumerableField<E>(
  d: FieldDescription<E>
): d is FieldDescription$EnumerableField<E> {
  return (
    d.type === FieldDescription$Type.THESAURUS ||
    Object.prototype.hasOwnProperty.call(d, 'thesaurus')
  );
}

export function isCustomField<E>(d: FieldDescription<E>): d is FieldDescription$Custom<E> {
  return d.type === FieldDescription$Type.CUSTOM;
}

export function isRouteField<E>(d: FieldDescription<E>): d is FieldDescription$RouteField<E> {
  return d.type === FieldDescription$Type.ROUTE;
}

export function isURLField<E>(d: FieldDescription<E>): d is FieldDescription$URLField<E> {
  return d.type === FieldDescription$Type.URL;
}
